import { useState } from 'react';
import { styled } from '@mui/material/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTranslation } from '@hooks';
import { PhoneFrame, BrandAvatar } from './styles';
import { toHandle, truncate } from './utils';
import { IgHeartIcon, IgCommentIcon, IgDmIcon, IgBookmarkIcon } from './PlatformIcons';
import MediaSlide from './MediaSlide';

const IG_FONT = '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif';
const TRUNCATE_AT = 125;

const Card = styled(PhoneFrame)({ fontFamily: IG_FONT, color: '#262626' });

const HeaderRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
});

const RingedAvatar = styled('div')({
    padding: 2,
    background: 'linear-gradient(45deg, #fdc468, #df4996, #c130d8)',
    borderRadius: '50%',
});

const Username = styled('div')({
    flex: 1,
    fontWeight: 600,
    fontSize: 14,
    color: '#262626',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const ActionsRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px 4px',
});

const ActionGroup = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flex: 1,
});

const ActionIcon = styled('div')({
    color: '#262626',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    '& svg': { fontSize: 24 },
});

const Likes = styled('div')({
    padding: '2px 12px',
    fontSize: 14,
    fontWeight: 600,
});

const Caption = styled('div')({
    padding: '4px 12px 12px',
    fontSize: 14,
    lineHeight: '18px',
    color: '#262626',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
});

const InlineUsername = styled('span')({ fontWeight: 600, marginRight: 6 });

const More = styled('button')({
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 14,
    color: '#8e8e8e',
    cursor: 'pointer',
});

const Hashtag = styled('span')({ color: '#00376b' });

const renderWithHashtags = text => {
    if (!text) return null;
    const parts = text.split(/(#[\p{L}\p{N}_]+)/gu);
    return parts.map((part, i) =>
        part.startsWith('#') ? <Hashtag key={i}>{part}</Hashtag> : <span key={i}>{part}</span>,
    );
};

const PlatformCardInstagram = ({ pageName, handle: realHandle, locationLogo, message, media }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const { shown, truncated } = expanded ? { shown: message, truncated: false } : truncate(message, TRUNCATE_AT);
    const handle = realHandle || toHandle(pageName);

    return (
        <Card>
            <HeaderRow>
                <RingedAvatar>
                    <BrandAvatar name={pageName} image={locationLogo} size={32} color="#c130d8" />
                </RingedAvatar>
                <Username>{handle}</Username>
                <MoreHorizIcon sx={{ color: '#262626' }} />
            </HeaderRow>

            <MediaSlide media={media} aspectRatio="1 / 1" />

            <ActionsRow>
                <ActionGroup>
                    <ActionIcon>
                        <IgHeartIcon />
                    </ActionIcon>
                    <ActionIcon>
                        <IgCommentIcon />
                    </ActionIcon>
                    <ActionIcon>
                        <IgDmIcon />
                    </ActionIcon>
                </ActionGroup>
                <ActionIcon>
                    <IgBookmarkIcon />
                </ActionIcon>
            </ActionsRow>

            <Likes>{t('SocialPosts.preview.likesPlaceholder')}</Likes>

            {message && (
                <Caption>
                    <InlineUsername>{handle}</InlineUsername>
                    {renderWithHashtags(shown)}
                    {truncated && (
                        <>
                            {' '}
                            <More type="button" onClick={() => setExpanded(true)}>
                                {t('SocialPosts.preview.more')}
                            </More>
                        </>
                    )}
                </Caption>
            )}
        </Card>
    );
};

export default PlatformCardInstagram;
